import { Bell, Repeat, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { TimeInputField } from "@/components/foundations/inputs/TimeInputField/TimeInputField";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TaskDisplayControls } from "@/components/tasks/options/TaskColorEditor/TaskColorEditor";
import { ListColorKey } from "@/lib/listColors";
import { roundTimeStringToStep } from "@/lib/time";

/**
 * Shared task-fields form. Single source of truth for the look and behavior of
 * every editable task field across the app:
 *   - TaskDetail (live task on a day)
 *   - ListEditor (template definition in Settings)
 *   - ListToDay (per-task overrides when adding an list to a day)
 *
 * Every optional field has a small × clear button that resets it to its empty/null
 * value, fixing the long-standing "can't clear a field once typed" bug.
 *
 * The component is intentionally controlled — it does not own any state. Callers
 * pass `value` + `onChange` and decide when to persist (typically on blur).
 *
 * Color rule: display controls stay shared with the pushed Display page. The
 * picker draft preserves colors while "inherit view" is on; persistence decides
 * whether inherited colors become null on save.
 */
export type TaskFieldsValue = {
  title: string;
  details: string;
  /** Notes are instance-only; templates pass undefined to hide the section. */
  notes?: string;
  startTime: string;        // "HH:MM" or ""
  durationMin: string;      // numeric string or ""
  alertEnabled: boolean;
  /** Priority 1-5, or null for no priority. */
  priority: number | null;
  /** Repeat block. If undefined the whole repeat section is hidden. */
  repeat?: {
    enabled: boolean;
    intervalMin: string;
    endType: "endTime" | "count";
    endTime: string;
    count: string;
  };
  customColors: boolean;
  barColor: ListColorKey | null;
  bgColor: ListColorKey | null;
  showTimeAccordion?: boolean;
};

export type TaskFieldsLabels = {
  /** "Default start" for templates, "Start" for instances. */
  startLabel?: string;
  /** Caption under the start field, e.g. defaults note for templates. */
  startCaption?: string;
  detailsLabel?: string;
  notesLabel?: string;
  /** Used in the color-preview card row 1 (e.g. list name). */
  previewLabel?: string;
  /** Fallback bar color used by ColorPreviewCard when none chosen. */
  fallbackBar?: string | null;
  /** Fallback background color used by ColorPreviewCard when none chosen. */
  fallbackBg?: string | null;
};

export type TaskFieldsAccent = {
  bar: string;
  soft: string;
  ink: string;
};

export type TaskFieldsCallbacks = {
  /** Called whenever any field changes locally. */
  onChange: (next: TaskFieldsValue) => void;
  /**
   * Optional: called on blur of an individual field. Allows callers to debounce
   * autosave to the server. Receives a partial describing the change so the
   * caller can ship the minimum needed payload.
   */
  onCommit?: (partial: Partial<TaskFieldsValue>, field: keyof TaskFieldsValue) => void;
};

/** Names of the blocks the form can render. */
export type TaskFieldsSection =
  | "title"
  | "details"
  | "notes"
  | "priority"
  | "timing"
  | "repeat"
  | "colors";

type Props = TaskFieldsCallbacks & {
  value: TaskFieldsValue;
  labels?: TaskFieldsLabels;
  /** Hide the title input (when caller renders title above the form). */
  hideTitle?: boolean;
  /**
   * Whitelist of sections to render and the order to render them in. Defaults
   * to all sections in their natural order. Allows callers (e.g. TaskDetail)
   * to interleave their own sections like Status / Log between blocks.
   */
  sections?: ReadonlyArray<TaskFieldsSection>;
  accent?: TaskFieldsAccent;
};

const ALL_SECTIONS: ReadonlyArray<TaskFieldsSection> = [
  "title",
  "details",
  "notes",
  "priority",
  "timing",
  "repeat",
  "colors",
];

export default function TaskFieldsForm({
  value: v,
  onChange,
  onCommit,
  labels = {},
  hideTitle = false,
  sections = ALL_SECTIONS,
  accent,
}: Props) {
  const set = <K extends keyof TaskFieldsValue>(key: K, val: TaskFieldsValue[K]) => {
    const next = { ...v, [key]: val };
    onChange(next);
    return next;
  };
  const commit = (partial: Partial<TaskFieldsValue>, field: keyof TaskFieldsValue) =>
    onCommit?.(partial, field);

  const repeat = v.repeat;
  const switchStyle = (checked: boolean) =>
    accent && checked ? { backgroundColor: accent.bar } : undefined;

  const renderTitle = () =>
    hideTitle ? null : (
      <FieldShell
        key="title"
        label="Title"
        onClear={v.title ? () => {
          set("title", "");
          commit({ title: "" }, "title");
        } : undefined}
        accent={accent}
      >
        <Input
          value={v.title}
          placeholder="Task title"
          onChange={e => set("title", e.target.value)}
          onBlur={() => commit({ title: v.title.trim() }, "title")}
        />
      </FieldShell>
    );

  const renderDetails = () => (
    <FieldShell
      key="details"
      label={labels.detailsLabel ?? "Details"}
      onClear={v.details ? () => {
        set("details", "");
        commit({ details: "" }, "details");
      } : undefined}
      accent={accent}
    >
      <Textarea
        rows={3}
        value={v.details}
        placeholder="Description shown in the day list…"
        onChange={e => set("details", e.target.value)}
        onBlur={() => commit({ details: v.details }, "details")}
      />
    </FieldShell>
  );

  const renderNotes = () =>
    v.notes === undefined ? null : (
      <FieldShell
        key="notes"
        label={labels.notesLabel ?? "Notes"}
        onClear={v.notes ? () => {
          set("notes", "");
          commit({ notes: "" }, "notes");
        } : undefined}
        accent={accent}
      >
        <Textarea
          rows={3}
          value={v.notes}
          placeholder="Private notes for this task…"
          onChange={e => set("notes", e.target.value)}
          onBlur={() => commit({ notes: v.notes ?? "" }, "notes")}
        />
      </FieldShell>
  );

  const renderTiming = () => (
    <div key="timing" className="ui-task-fields-form__timing-row grid grid-cols-2 gap-3">
      <FieldShell
        label={labels.startLabel ?? "Start"}
        caption={labels.startCaption}
        onClear={v.startTime ? () => {
          set("startTime", "");
          commit({ startTime: "" }, "startTime");
        } : undefined}
        accent={accent}
        className="min-w-0"
        compact
      >
        <TimeInputField
          value={v.startTime}
          onChange={newTime => {
            set("startTime", newTime);
            commit({ startTime: newTime }, "startTime");
          }}
          borderColor={accent?.bar}
        />
      </FieldShell>
      <FieldShell
        label="Duration"
        onClear={v.durationMin ? () => {
          set("durationMin", "");
          commit({ durationMin: "" }, "durationMin");
        } : undefined}
        accent={accent}
        className="min-w-0"
        compact
      >
        <Input
          type="number"
          inputMode="numeric"
          pattern="[0-9]*"
          min={1}
          value={v.durationMin}
          placeholder="optional"
          onChange={e => set("durationMin", e.target.value)}
          onBlur={() => commit({ durationMin: v.durationMin }, "durationMin")}
        />
      </FieldShell>
      <FieldShell
        label="Alert"
        accent={accent}
        className="min-w-0 cursor-pointer"
        compact
        role="button"
        tabIndex={0}
        onClick={() => {
          const next = !v.alertEnabled;
          set("alertEnabled", next);
          commit({ alertEnabled: next }, "alertEnabled");
        }}
        onKeyDown={e => {
          if (e.key !== "Enter" && e.key !== " ") return;
          e.preventDefault();
          const next = !v.alertEnabled;
          set("alertEnabled", next);
          commit({ alertEnabled: next }, "alertEnabled");
        }}
      >
        <div className="h-9 flex items-center gap-1.5">
          <div
            className="font-display text-base inline-flex items-center gap-1.5"
            style={accent ? { color: accent.ink } : undefined}
          >
            <Bell
              className="h-4 w-4"
              color={accent?.bar ?? "currentColor"}
              fill={v.alertEnabled ? accent?.bar ?? "currentColor" : "none"}
            />
            {v.alertEnabled ? "On" : "Off"}
          </div>
        </div>
      </FieldShell>
    </div>
  );

  const renderRepeat = () =>
    repeat === undefined ? null : (
      <div key="repeat" className="ui-task-fields-form__repeat bg-card border border-border/60 rounded-xl p-4 space-y-3">
        <div className="ui-task-fields-form__repeat-header flex items-center justify-between">
          <div>
            <Label
              className="cursor-pointer inline-flex items-center gap-2"
              style={accent ? { color: accent.ink } : undefined}
            >
              <Repeat className="h-3.5 w-3.5" />
              Repeat
            </Label>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Generate this task on a cadence within the day.
            </p>
          </div>
          <Switch
            checked={repeat.enabled}
            style={switchStyle(repeat.enabled)}
            onCheckedChange={val => {
              const next = { ...repeat, enabled: val };
              set("repeat", next);
              commit({ repeat: next }, "repeat");
            }}
          />
        </div>
        {repeat.enabled && (
          <div className="ui-task-fields-form__repeat-body space-y-3 pt-2">
            <FieldShell label="Every (minutes)" accent={accent}>
              <Input
                type="number"
                inputMode="numeric"
                pattern="[0-9]*"
                min={1}
                value={repeat.intervalMin}
                onChange={e => {
                  const next = { ...repeat, intervalMin: e.target.value };
                  set("repeat", next);
                }}
                onBlur={() => commit({ repeat }, "repeat")}
              />
            </FieldShell>
            <div className="ui-task-fields-form__repeat-end space-y-2">
              <Label style={accent ? { color: accent.ink } : undefined}>End condition</Label>
              <Select
                value={repeat.endType}
                onValueChange={val => {
                  const next = { ...repeat, endType: val as "endTime" | "count" };
                  set("repeat", next);
                  commit({ repeat: next }, "repeat");
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="endTime">End at a time</SelectItem>
                  <SelectItem value="count">Number of times</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {repeat.endType === "endTime" ? (
              <FieldShell
                label="End time"
                onClear={repeat.endTime ? () => {
                  const next = { ...repeat, endTime: "" };
                  set("repeat", next);
                  commit({ repeat: next }, "repeat");
                } : undefined}
                accent={accent}
              >
                <TimeInputField
                  value={repeat.endTime}
                  onChange={e => {
                    const next = { ...repeat, endTime: e };
                    set("repeat", next);
                    commit({ repeat: next }, "repeat");
                  }}
                  borderColor={accent?.bar}
                />
              </FieldShell>
            ) : (
              <FieldShell
                label="Repeat count"
                onClear={repeat.count ? () => {
                  const next = { ...repeat, count: "" };
                  set("repeat", next);
                  commit({ repeat: next }, "repeat");
                } : undefined}
                accent={accent}
              >
                <Input
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  min={1}
                  value={repeat.count}
                  onChange={e => {
                    const next = { ...repeat, count: e.target.value };
                    set("repeat", next);
                  }}
                  onBlur={() => commit({ repeat }, "repeat")}
                />
              </FieldShell>
            )}
          </div>
        )}
      </div>
    );

  const renderColors = () => (
    <TaskDisplayControls
      key="colors"
      value={{
        inheritView: !v.customColors,
        barColor: v.barColor,
        bgColor: v.bgColor,
      }}
      onChange={display => {
        const next: TaskFieldsValue = {
          ...v,
          customColors: !display.inheritView,
          barColor: display.barColor,
          bgColor: display.bgColor,
        };
        onChange(next);
        commit(
          {
            customColors: next.customColors,
            barColor: next.barColor,
            bgColor: next.bgColor,
          },
          "customColors",
        );
      }}
      previewTitle={v.title}
      previewLabel={labels.previewLabel ?? "List"}
      fallbackBar={labels.fallbackBar ?? null}
      fallbackBg={labels.fallbackBg ?? null}
      accent={accent}
    />
  );

  const renderSection = (s: TaskFieldsSection) => {
    switch (s) {
      case "title": return renderTitle();
      case "details": return renderDetails();
      case "notes": return renderNotes();
      case "timing": return renderTiming();
      case "repeat": return renderRepeat();
      case "colors": return renderColors();
      default: return null;
    }
  };

  return (
    <div className="ui-task-fields-form space-y-4">
      {sections.map(s => renderSection(s))}
    </div>
  );
}

/**
 * Field card shell with a label, optional caption, optional clear button,
 * and a content slot. Matches the rounded-xl card style used everywhere.
 */
function FieldShell({
  label,
  caption,
  onClear,
  accent,
  className,
  compact = false,
  role,
  tabIndex,
  onClick,
  onKeyDown,
  children,
}: {
  label: string;
  caption?: string;
  onClear?: () => void;
  accent?: TaskFieldsAccent;
  className?: string;
  compact?: boolean;
  role?: React.AriaRole;
  tabIndex?: number;
  onClick?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`ui-task-field bg-card border border-border/60 rounded-xl ${compact ? "p-3" : "p-4"} space-y-2 ${className ?? ""}`}
      role={role}
      tabIndex={tabIndex}
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      <div className="ui-task-field__header flex items-center justify-between">
        <div
          className="ui-task-field__label text-[11px] uppercase tracking-wider text-muted-foreground"
          style={accent ? { color: accent.ink, opacity: 0.68 } : undefined}
        >
          {label}
        </div>
        <button
          type="button"
          onMouseDown={e => e.preventDefault()}
          onClick={onClear}
          disabled={!onClear}
          className={`ui-task-field__clear inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors ${onClear ? "" : "invisible"}`}
          style={accent ? { color: accent.ink } : undefined}
          aria-label={`Clear ${label.toLowerCase()}`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {children}
      {caption && (
        <p
          className="ui-task-field__caption text-[11px] text-muted-foreground"
          style={accent ? { color: accent.ink, opacity: 0.68 } : undefined}
        >
          {caption}
        </p>
      )}
    </div>
  );
}
